import type { Column, Row } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ComboBox } from "./combo-box";
import { Button } from "../ui";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { format } from "date-fns";
import { NaBadge } from "./NaBadge";
import type { FormOption } from "~/types/ui";

export function SearchHeader({
  child,
  column,
  combo = undefined,
  childIsText = false,
  isMulti = true,
  setComboValue = null,
  comboValue = null,
}: {
  child: string;
  column: Column<any, unknown>;
  combo?: any[];
  childIsText?: boolean;
  isMulti?: boolean;
  setComboValue?: ((value: string[] | string | null) => void) | null;
  comboValue?: string[] | string | null;
}) {
  const { t } = useTranslation();
  const [localComboValue, localSetComboValue] = useState<string[] | string>([]);
  useEffect(() => {
    if (comboValue) {
      localSetComboValue(comboValue);
      column.setFilterValue(comboValue);
    }
  }, [comboValue]);

  const comboChild = combo ? (
    <ComboBox
      value={localComboValue}
      placeholder={childIsText ? child : t(child)}
      isMulti={isMulti}
      options={combo.map((v) => {
        return { value: v, label: v } as FormOption<any>;
      })}
      clearable
      onChange={(v) => {
        localSetComboValue(v as string[]);
        setComboValue?.(v as string[]);
        column.setFilterValue(v);
      }}
    />
  ) : null;

  return (
    <div className="max-w[100%] flex justify-start align-center items-center">
      <div>
        {combo ? (
          comboChild
        ) : (
          <span className="justify-center align-center">
            {childIsText ? child : t(child)}
          </span>
        )}
      </div>
      <Button
        variant="ghost"
        className="px-0 font-semibold"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        {column.getIsSorted() === "asc" ? (
          <ArrowUp className="ml-2 h-4 w-4" />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowDown className="ml-2 h-4 w-4" />
        ) : (
          <ArrowUpDown className="ml-2 h-4 w-4" />
        )}
      </Button>
    </div>
  );
}

export function DateDisplay({
  dateString,
}: {
  dateString: string | null | undefined;
}) {
  if (!dateString) return <NaBadge />;

  const d = format(new Date(dateString), "dd MMM - HH:mm");
  return <span>{d}</span>;
}

export interface RowDetailsProps {
  row: Row<any>;
}

export function RowDetails({ row }: RowDetailsProps) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    let mounted = true;

    async function fetchData() {
      setLoading(true);
      try {
        // 🔹 Replace with your real API call
        const response = await new Promise((resolve) =>
          setTimeout(
            () => resolve({ details: `Extra data for row ${row.id}` }),
            1000,
          ),
        );

        if (mounted) {
          setData(response);
        }
      } catch (err) {
        console.error("Failed to fetch row details", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchData();

    return () => {
      mounted = false;
    };
  }, [row]);

  if (loading) {
    return (
      <div className="p-4 text-sm">⏳ Loading details for row {row.id}...</div>
    );
  }

  return (
    <div className="p-4">
      <h4 className="font-semibold mb-2">
        Details for ioasjdfoijsdof jsdf sdiojf sio dfsio dfjsoi fjsio jfsoidj
        fsoidjf soidfj oisjf oisjf iosfj osifj s iofjso ifjsio jfsoi fjsoifj
        soif osifjsio fjsiof jsiofj iosfj soifjos ijfrow {row.id}
      </h4>
      <pre className="text-sm">{JSON.stringify(data, null, 2)}</pre>
    </div>
  );
}
